import i18next from 'i18next'

import { apiService as bulkApiService } from '../api/bulk-import-api.service'
import { placesService } from '../../service/api/places.service'

const delay = milliseconds =>
  new Promise(resolve => setTimeout(resolve, milliseconds))

const isVersionValid = sheetData => {
  const firstRow = Object.values(sheetData[0])
  const latestVersionNumber = i18next.t(
    'bulk__delivery_latest_template_version'
  )
  return firstRow.some(value => value === latestVersionNumber)
}

const parseDropoffTimeValue = sheetData =>
  sheetData.map(order => {
    const { delivery_type: deliveryType, ...rest } = order
    let deliveryTypeValue = ''

    const instantDeliveryValue = i18next.t(
      'orders.new.basicInfo.selection.instant'
    )
    const speedDeliveryValue = i18next.t('orders.new.basicInfo.selection.4hour')

    if (deliveryType === instantDeliveryValue) {
      deliveryTypeValue = 'instant'
    } else if (
      deliveryType === speedDeliveryValue ||
      deliveryType === '4-hour' ||
      deliveryType === '4 hour'
    ) {
      deliveryTypeValue = '4 hour'
    } else {
      deliveryTypeValue = deliveryType
    }

    return {
      ...rest,
      delivery_type: deliveryTypeValue,
    }
  })

const parseCoordinates = orders =>
  orders.map(order => {
    const {
      shipper_latitude: shipperLatitude,
      shipper_longitude: shipperLongtitude,
      recipient_latitude: recipientLatitude,
      recipient_longitude: recipientLongitude,
      ...rest
    } = order

    return {
      ...(shipperLatitude && {
        shipper_address_latitude: shipperLatitude.toString(),
      }),
      ...(shipperLongtitude && {
        shipper_address_longitude: shipperLongtitude.toString(),
      }),
      ...(recipientLatitude && {
        recipient_address_latitude: recipientLatitude.toString(),
      }),
      ...(recipientLongitude && {
        recipient_address_longitude: recipientLongitude.toString(),
      }),
      ...rest,
    }
  })

const parseSizeToDimensions = orders =>
  orders.map(order => {
    const { size, ...rest } = order
    const packageSizeDimensions = size ? size.split('x') : null
    if (packageSizeDimensions === null) return order

    return {
      length: packageSizeDimensions[0],
      width: packageSizeDimensions[1],
      height: packageSizeDimensions[2],
      size,
      ...rest,
    }
  })

const formatPayloadForBulkQuote = sheetData => {
  const ROW_DATA_STARTS_IN_SHEET = 11
  const orderData = {
    orders: [],
  }

  orderData.orders = sheetData.map(({ id, ...rest }, index) => ({
    ...rest,
    row: `${index + ROW_DATA_STARTS_IN_SHEET}`,
  }))
  return orderData
}

const handleSeparateLWH = payload => {
  const orderData = {
    orders: [],
  }

  orderData.orders = payload.orders.map((order, index) => {
    const { length, width, height } = order
    if (!!length && !!width && !!height) {
      return { ...order, size: `${length}x${width}x${height}` }
    }
    return order
  })

  return orderData
}

const injectErrorsIntoOrderData = (orderFailures, ordersList) => {
  const copiedOrders = ordersList.map((order, index) => {
    const copiedOrder = { ...order }
    if (!copiedOrder.row) {
      copiedOrder.row = String(index)
    }
    return copiedOrder
  })

  Object.entries(orderFailures).forEach(([row, errorsOnRow]) => {
    errorsOnRow.forEach(({ code }) => {
      const propertyName = `${code.split('.')[1]}_error`
      copiedOrders.find(order => order.row === row)[propertyName] = code
    })
  })

  return copiedOrders
}

const injectWarningIntoQuotationResponse = (
  quotationPayload,
  quotationResponse
) => {
  const copiedOrders = quotationResponse.map(order => ({ ...order }))

  for (let i = 0; i < quotationPayload.length; i++) {
    if (quotationPayload[i].shipper_address_warning) {
      copiedOrders[i].shipper_address_warning =
        quotationPayload[i].shipper_address_warning
    }

    if (quotationPayload[i].recipient_address_warning) {
      copiedOrders[i].recipient_address_warning =
        quotationPayload[i].recipient_address_warning
    }
  }

  return copiedOrders
}

const filterUnwantedProperties = ordersList =>
  ordersList.map(order => {
    const {
      price,
      shipper_address_latitude,
      shipper_address_longitude,
      recipient_address_latitude,
      recipient_address_longitude,
      ...rest
    } = order

    return { ...rest }
  })

const filterErrorProperties = ordersList =>
  ordersList.map(order => {
    const wantedProperties = {}
    const endsWithErrorKey = /_error$/

    Object.keys(order).forEach(key => {
      if (!endsWithErrorKey.exec(key)) {
        wantedProperties[key] = order[key]
      }
    })

    return wantedProperties
  })

const validatePickupTime = (ordersList = [], errors = {}) => {
  const errorsCopy = { ...errors }
  ordersList.forEach((order, index) => {
    const { pickup_time: pickupTime, row = index } = order

    const hour = pickupTime && Number.parseInt(pickupTime.split(':')[0], 10)
    const minutes = pickupTime && Number.parseInt(pickupTime.split(':')[1], 10)

    if (
      [20, 21].includes(hour) ||
      (hour === 19 && minutes > 50) ||
      (hour === 22 && minutes === 0)
    ) {
      const errorObject = {
        data: pickupTime,
        code: 'error.pickup_time.out_of_service_hour',
      }
      if (errorsCopy[row]) {
        errorsCopy[row].push(errorObject)
      } else {
        errorsCopy[row] = [errorObject]
      }
    }
  })

  return errorsCopy
}

const validateAddressDetail = (ordersList = [], errors = {}) => {
  const errorsCopy = { ...errors }
  ordersList.forEach(order => {
    const {
      shipper_address_details: shipperAddressDetails,
      recipient_address_details: recipientAddressDetails,
      row,
    } = order

    if (!shipperAddressDetails || !recipientAddressDetails) {
      if (!errorsCopy[row]) {
        errorsCopy[row] = []
      }
    }

    if (!shipperAddressDetails) {
      const errorObject = {
        data: null,
        code: 'error.shipper_address_details.missing',
      }
      errorsCopy[row].push(errorObject)
    }

    if (!recipientAddressDetails) {
      const errorObject = {
        data: null,
        code: 'error.recipient_address_details.missing',
      }
      errorsCopy[row].push(errorObject)
    }
  })

  return errorsCopy
}

const getFormattedALSAddressResultAndScore = addressObject => {
  const invalidAddress = addressObject.RequestAddress.AddressLine[0]
  const first = addressObject.SuggestedAddress[0]
  const score = first?.ValidationInformation?.Score || 0
  const ALSEngPremisesAddress =
    first?.Address?.PremisesAddress?.EngPremisesAddress
  if (ALSEngPremisesAddress) {
    const buildingName = ALSEngPremisesAddress?.BuildingName || ''
    const hasBlock = ALSEngPremisesAddress?.EngBlock
    const formattedBuildingName = hasBlock
      ? buildingName.replace('HSE', 'HOUSE').replace(/\(.*\)/, '')
      : ''
    const streetName = ALSEngPremisesAddress?.EngStreet?.StreetName || ''
    const estateName = ALSEngPremisesAddress?.EngEstate?.EstateName || ''
    const villageName = ALSEngPremisesAddress?.EngVillage?.VillageName || ''
    const buildingNumber =
      ALSEngPremisesAddress?.EngStreet?.BuildingNoFrom || formattedBuildingName
    const address = streetName || estateName || villageName
    const district = ALSEngPremisesAddress?.EngDistrict?.DcDistrict || ''
    const queryAddress =
      !buildingNumber && !address
        ? ''
        : `${buildingNumber} ${address} ${district}`.replace('&', 'and')
    return { invalidAddress, queryAddress, score }
  }
  return { invalidAddress, queryAddress: '', score }
}

const getInvalidAddressListFromBulkOrders = ordersList => {
  const addressList = []
  ordersList.forEach(order => {
    const {
      shipper_address_error: shipperAddressError,
      recipient_address_error: recipientAddressError,
      shipper_address: shipperAddress,
      recipient_address: recipientAddress,
    } = order

    if (
      /(_address.not_recognized|_address.invalid)$/.test(shipperAddressError)
    ) {
      addressList.push(shipperAddress)
    }

    if (
      /(_address.not_recognized|_address.invalid)$/.test(recipientAddressError)
    ) {
      addressList.push(recipientAddress)
    }
  })

  return [...new Set(addressList)]
}

const getALSAddressAndThenVerify = async address => {
  const emptyResult = {}

  try {
    const ALSResult = await bulkApiService.getALSAddress(address)

    // no result from ALS
    if (!ALSResult.SuggestedAddress) return emptyResult
    const {
      invalidAddress,
      queryAddress,
      score,
    } = getFormattedALSAddressResultAndScore(ALSResult)

    // score below threshold so not needed
    if (score < 70) return emptyResult

    const autoCompleteResult = await placesService.placesAutocomplete(
      queryAddress
    )
    if (autoCompleteResult.length > 0) {
      const newAddress = autoCompleteResult[0].description
      const confidence = score >= 80 ? 'HIGH' : 'LOW'
      const res = { originalAddress: invalidAddress, newAddress, confidence }
      return res
    }    
  } catch (error) {
    console.error('getALSAddressAndThenVerify failed', error)
  }

  return emptyResult
}

const getAddressSuggestionObjectList = async invalidAddressList => {
  const promiseArray = invalidAddressList.map((address, index) =>
    delay(index * 300).then(() => getALSAddressAndThenVerify(address))
  )
  const results = await Promise.allSettled(promiseArray)
  const suggestedAddressList = {}

  results.forEach(({ value = {} }) => {
    if (Object.keys(value).length > 0) {
      suggestedAddressList[value.originalAddress] = value
    }
  })

  return suggestedAddressList
}

const getInvalidAddressAndReturnAddressSuggestions = async bulkOrderData => {
  try {
    const invalidAddressList = getInvalidAddressListFromBulkOrders(
      bulkOrderData
    )
    const result = await getAddressSuggestionObjectList(invalidAddressList)
    return result
  } catch (error) {
    console.log(error)
  }
}

const copyAddressIntoAddressDetails = sheetData =>
  sheetData.map(order => {
    const copy = { ...order }
    copy.recipient_address_details = copy.recipient_address
    copy.shipper_address_details = copy.shipper_address

    return copy
  })

const replaceAddressWithSuggestions = (orderList, suggestedAddressListObject) =>
  orderList.map(order => {
    const copy = { ...order }

    if (suggestedAddressListObject[copy.shipper_address]) {
      copy.shipper_address_warning =
        suggestedAddressListObject[copy.shipper_address].confidence === 'LOW'
          ? 'warning.low_score'
          : undefined
      copy.shipper_address =
        suggestedAddressListObject[copy.shipper_address].newAddress
      delete copy.shipper_address_error
    }

    if (suggestedAddressListObject[copy.recipient_address]) {
      copy.recipient_address_warning =
        suggestedAddressListObject[copy.recipient_address].confidence === 'LOW'
          ? 'warning.low_score'
          : undefined
      copy.recipient_address =
        suggestedAddressListObject[copy.recipient_address].newAddress
      delete copy.recipient_address_error
    }

    return copy
  })

const removeWhiteSpace = value => value.replaceAll(/\s/g, '')

const formatColumns = (orderList, columnList, formatFunction) =>
  orderList.map(order => {
    const copy = { ...order }

    columnList.forEach(columnName => {
      if (copy.hasOwnProperty(columnName)) {
        copy[columnName] = formatFunction(copy[columnName])
      }
    })

    return copy
  })

const orderListContainsError = orderList => {
  for (const order of orderList) {
    const orderContainsError = Object.keys(order).some(
      key => key.endsWith('_error') && typeof order[key] === 'string'
    )
    if (orderContainsError) {
      return true
    }
  }

  return false
}

const validateForShopify = (
  orders,
  { defaultErrors = {} }
) => {
  let validationErrors = { ...defaultErrors }

  validationErrors = validatePickupTime(orders, validationErrors)

  validationErrors = validateAddressDetail(orders, validationErrors)
  
  /**
   * Pickup_date, pickup_time, delivery_type(Drop-off time) are necessary for checking the recipient_address is acceptable or not.
   * So if the order import from Shopify lack of information about those columns we should mark columns of address as invalid.
   */
  orders.forEach((order, index) => {
    const {
      pickup_date,
      pickup_time,
      delivery_type,
      shipper_address,
      recipient_address,
      row = index,
    } = order

    if (!pickup_date || !pickup_time || !delivery_type) {
      const addressErrors = [
        { data: shipper_address, code: 'error.shipper_address.invalid' },
        { data: recipient_address, code: 'error.recipient_address.invalid' },
      ]

      if (validationErrors[row]) {
        validationErrors[row].push(...addressErrors)
      } else {
        validationErrors[row] = addressErrors
      }
    }
  })

  return validationErrors
}

export {
  isVersionValid,
  parseDropoffTimeValue,
  parseCoordinates,
  parseSizeToDimensions,
  formatPayloadForBulkQuote,
  handleSeparateLWH,
  injectErrorsIntoOrderData,
  filterUnwantedProperties,
  filterErrorProperties,
  validatePickupTime,
  validateAddressDetail,
  getInvalidAddressAndReturnAddressSuggestions,
  copyAddressIntoAddressDetails,
  replaceAddressWithSuggestions,
  getInvalidAddressListFromBulkOrders,
  getALSAddressAndThenVerify,
  getAddressSuggestionObjectList,
  injectWarningIntoQuotationResponse,
  removeWhiteSpace,
  formatColumns,
  orderListContainsError,
  validateForShopify,
}
